import { Fragment, useState } from "react";
import { uniqBy } from "lodash";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Grid3X3 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Lightning } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { VariantGeneratorModal } from "@/components/ai-designer/variant-generator";
import { PlatformAdapterModal } from "@/components/ai-designer/platform-adapter";
import { ProductPhotoshootModal } from "@/components/ai-designer/product-photoshoot";
import { UGCAdCreatorModal } from "@/components/ai-designer/ugc-ad-creator";

import { BrandCommonApiFactory } from "@/apis/brand-os/brand-common";
import { BrandProduct } from "@/schema/brand-os/brand-common";
import { mCreativeApps, CreativeApp, AppType } from "@/constants/creative-apps";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/dashboard/ai-designer/_layout/apps")({
  component: Apps,
});

function Apps() {
  const [selectedApp, setSelectedApp] = useState<AppType | null>(null);

  const { data: productsResponse, isLoading: isLoadingProducts } = useQuery({ ...BrandCommonApiFactory.Queries.GetProducts(), select: (res) => uniqBy(res?.data?.products ?? [], "id") });
  const { data: subBrandsResponse, isLoading: isLoadingSubBrands } = useQuery({ ...BrandCommonApiFactory.Queries.GetSubBrands(), select: (res) => uniqBy(res?.data.sub_brands ?? [], "id") });
  const products = productsResponse ?? [];
  const subBrands = subBrandsResponse ?? [];
  const totalApps = mCreativeApps.flatMap((section) => section.apps).length;

  return (
    <Fragment>
      <div className="flex items-center gap-4 mb-6">
        <Grid3X3 className="size-6" />
        <div>
          <h3 className="text-lg font-semibold">Apps</h3>
          <p className="text-sm text-muted-foreground">{totalApps} specialized AI creative agents</p>
        </div>
      </div>
      <div className="space-y-10">
        {mCreativeApps.map((section) => (
          <section key={section.id}>
            <div className="mb-5 flex flex-col md:flex-row gap-2 md:gap-4 items-baseline md:items-center">
              <h3 className="text-lg font-semibold">{section.title}</h3>
              <p className="text-sm text-muted-foreground">{section.subtitle}</p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {section.apps.map((app) => (
                <AppCard key={app.id} app={app} onSelectApp={setSelectedApp} />
              ))}
            </div>
          </section>
        ))}
        {selectedApp && (
          <AppModal appId={selectedApp} onClose={() => setSelectedApp(null)} products={products} isLoadingProducts={isLoadingProducts} subBrands={subBrands} isLoadingSubBrands={isLoadingSubBrands} />
        )}
      </div>
    </Fragment>
  );
}

function AppCard({ app, onSelectApp }: { app: CreativeApp; onSelectApp: (id: AppType) => void }) {
  const handleClick = () => {
    onSelectApp(app.id);
  };

  return (
    <Card className="group cursor-pointer transition-all not-disabled:hover:shadow-2xl shadow-gray-400/15 gap-4" onClick={handleClick}>
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
        <div className={cn("rounded-md size-12 flex items-center justify-center shrink-0", app.colors.background, app.colors.text)}>
          <app.Icon weight="regular" className="size-6" />
        </div>
        <Badge variant="outline" className="flex items-center gap-0.5 bg-muted border-none">
          <Lightning weight="fill" className="size-3 fill-yellow-600" />
          <span className="text-xs text-foreground font-medium">
            {app.creditsLow} - {app.creditsHigh} Credits
          </span>
        </Badge>
      </CardHeader>
      <CardContent className="flex flex-col gap-1.5 pt-0">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-sm">{app.title}</p>
        </div>
        <p className="text-sm text-muted-foreground">{app.description}</p>
      </CardContent>
    </Card>
  );
}

interface AppModalProps {
  appId: AppType;
  onClose: () => void;
  products: BrandProduct[];
  isLoadingProducts: boolean;
  subBrands: string[];
  isLoadingSubBrands: boolean;
}

function AppModal({ appId, onClose, products, isLoadingProducts, subBrands, isLoadingSubBrands }: AppModalProps) {
  const props = { open: true, onOpenChange: (open: boolean) => !open && onClose() };
  switch (appId) {
    case "variant-generator":
      return <VariantGeneratorModal {...props} />;
    case "platform-adapter":
      return <PlatformAdapterModal {...props} />;
    case "product-photoshoot":
      return <ProductPhotoshootModal {...props} products={products} isLoadingProducts={isLoadingProducts} />;
    case "ugc-videos":
      return <UGCAdCreatorModal {...props} subBrands={subBrands} isLoadingSubBrands={isLoadingSubBrands} />;
  }
}
